import { useSyncExternalStore } from 'react';
import { apiRoutes } from '../../routes/api-routes';
import { sessionApi } from './http';
import type { components } from './schema';

export type SessionUser = components['schemas']['UserDto'];
type IssuedSession = components['schemas']['SessionDto'];

export interface SessionState {
  /** In memory only: never written to localStorage, where any script could read it. */
  accessToken: string | null;
  user: SessionUser | null;
}

const SIGNED_OUT: SessionState = { accessToken: null, user: null };
/** Refresh a minute before the access token expires. */
const REFRESH_MARGIN_SECONDS = 60;
/** After a network failure (laptop waking up, Wi-Fi not back yet), try again this much later. */
const NETWORK_RETRY_SECONDS = 15;
/** Another tab refreshed at the same moment (409): its new cookie is in place a moment later. */
const SUPERSEDED_RETRY_MS = 500;
const LOCK_NAME = 'sk-session';

let state: SessionState = SIGNED_OUT;
const listeners = new Set<() => void>();
let refreshTimer: ReturnType<typeof setTimeout> | undefined;
let refreshing: Promise<boolean> | undefined;
/** Increases on every sign-in and sign-out: a refresh that started before is out of date. */
let epoch = 0;

function publish(next: SessionState): void {
  state = next;
  for (const listener of listeners) listener();
}

function scheduleRefresh(seconds: number): void {
  clearTimeout(refreshTimer);
  refreshTimer = setTimeout(() => void session.refresh(), seconds * 1000);
}

export function getSession(): SessionState {
  return state;
}

export function subscribeToSession(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Runs `task` while no other tab of this browser is refreshing or signing in (Web Locks API).
 * They share one refresh cookie, so they must take turns or they would replace each other's token.
 */
export function exclusive<T>(task: () => Promise<T>): Promise<T> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  return locks ? locks.request(LOCK_NAME, task) : task();
}

/**
 * The signed-in session, shared by the API client, the WebSocket and the screens.
 * The refresh token lives in an httpOnly cookie the browser sends to /api/auth; scripts never see it.
 */
export const session = {
  get: getSession,
  subscribe: subscribeToSession,

  start(issued: IssuedSession): void {
    epoch += 1;
    scheduleRefresh(Math.max(issued.expiresIn - REFRESH_MARGIN_SECONDS, 10));
    publish({ accessToken: issued.accessToken, user: issued.user });
  },

  clear(): void {
    epoch += 1;
    clearTimeout(refreshTimer);
    publish(SIGNED_OUT);
  },

  /** Asks for a new access token with the refresh cookie. Concurrent callers in this tab share one request. */
  refresh(): Promise<boolean> {
    refreshing ??= requestRefresh().finally(() => {
      refreshing = undefined;
    });
    return refreshing;
  },
};

type RefreshOutcome =
  { kind: 'renewed'; issued: IssuedSession } | { kind: 'ended' } | { kind: 'unreachable' };

async function requestRefresh(): Promise<boolean> {
  const startedAt = epoch;
  const outcome = await exclusive(() => callRefresh());
  // Signed in or out while this was on its way: that newer state wins.
  if (epoch !== startedAt) return state.accessToken !== null;
  if (outcome.kind === 'renewed') {
    session.start(outcome.issued);
    return true;
  }
  if (outcome.kind === 'ended') session.clear();
  // Unreachable is not a sign-out: keep the session and try again soon.
  else if (state.accessToken) scheduleRefresh(NETWORK_RETRY_SECONDS);
  return false;
}

async function callRefresh(retried = false): Promise<RefreshOutcome> {
  try {
    const { data, response } = await sessionApi.POST(apiRoutes.auth.refresh);
    if (response.ok && data) return { kind: 'renewed', issued: data };
    if (response.status === 409 && !retried) {
      await new Promise((resolve) => setTimeout(resolve, SUPERSEDED_RETRY_MS));
      return await callRefresh(true);
    }
    // 429 and 5xx are server trouble, not the end of the session.
    if (response.status === 429 || response.status >= 500) return { kind: 'unreachable' };
    return { kind: 'ended' };
  } catch {
    return { kind: 'unreachable' };
  }
}

/** React view of the session; re-renders when it changes. */
export function useSession(): SessionState {
  return useSyncExternalStore(subscribeToSession, getSession, getSession);
}
